// src/lib/guided/ownerContact.ts
//
// Plan 5.3 of docs/guided-shopping_plan.md: "a visible way to reach a
// person" -- research §2.6's design response to "buyers dislike bots", and
// §2.4's Gartner finding (n=5,728) that losing access to a person is
// customers' top fear of AI-run service. One function shared by both
// visitor renderers (GuidedFunnelRenderer.tsx, kiroku-embed.ts's
// GuidedFunnelWidget) so they can never render a different link, or a link
// only one of them shows -- bug-037's "one evaluator, many renderers"
// discipline applied to a display decision, the same reasoning 5.2's
// fixedPathLength already used for the step counter.
import type { FunnelOwnerContact } from "./config";

export interface OwnerContactLink {
  href: string;
  text: string;
}

/**
 * Phone wins when the owner set both: it is the more direct "reach a
 * person" channel the research names, and showing two links at once would
 * stop being "quiet" (the task's own word). The `tel:` href strips
 * everything but a leading `+` and digits, matching how phone dialers
 * already normalise a typed number; the visible text keeps the owner's own
 * formatting. Returns null when neither is configured -- the field is
 * optional, and a flow with no contact set shows nothing rather than an
 * empty or broken link.
 */
export function ownerContactLink(
  contact: FunnelOwnerContact | undefined,
): OwnerContactLink | null {
  if (contact?.phone) {
    return {
      href: `tel:${contact.phone.replace(/[^\d+]/g, "")}`,
      text: `Call us: ${contact.phone}`,
    };
  }
  if (contact?.email) {
    return {
      href: `mailto:${contact.email}`,
      text: `Email us: ${contact.email}`,
    };
  }
  return null;
}
